<template>
  <div
    v-if="
      AppMenu &&
      AppMenu.FooterItems &&
      !hideNavigationBar &&
      !currentRoute.meta.hideFooterNavigator &&
      !IsMustChangePasswordActive
    "
    class="footer-navigation-container relative m:hidden"
  >
    <!-- No safe-area padding here: the brand-gradient strip below this component
         in App.vue already covers --ion-safe-area-bottom; padding for it again
         floats the dock a full inset above the strip. -->
    <div class="footer-bar center-xy px-3 pt-1 pb-1">
      <nav
        class="nav-dock flex w-full items-center gap-0.5 rounded-full p-0.5 backdrop-blur-xl backdrop-saturate-150"
      >
        <CustomIonButton
          v-for="item in primaryItems"
          :key="item.ViewRoute || item.MenuName"
          fill="clear"
          class="nav-tab m-0 flex-1"
          :class="{ 'nav-tab--active': currentRoute.path === item.ViewRoute }"
          :ariaLabel="item.FooterTitle || item.DisplayTitle || undefined"
          @onClick="onNavClick(item)"
        >
          <span class="col-center gap-0.5 leading-m">
            <i
              class="leading-none text-m"
              :class="[
                getTablerIconClass(item.FooterIcon ?? item.MenuIcon),
                { 'nav-tab__icon--active': currentRoute.path === item.ViewRoute },
              ]"
              aria-hidden="true"
            ></i>
            <span class="text-xxxs font-semibold whitespace-nowrap leading-m">
              {{ item.FooterTitle || item.DisplayTitle }}
            </span>
          </span>
        </CustomIonButton>
        <template v-if="hasOverflow">
          <div class="nav-divider mx-0.5 h-7 w-px shrink-0" aria-hidden="true"></div>
          <CustomIonButton
            fill="clear"
            class="nav-tab m-0 flex-1"
            :class="{ 'nav-tab--active': isMoreOpen }"
            ariaLabel="More options"
            @onClick="onMoreClick"
          >
            <span class="col-center gap-0.5 leading-m">
              <i class="ti ti-dots text-m leading-none" aria-hidden="true"></i>
              <span class="text-xxxs font-semibold whitespace-nowrap leading-m">More</span>
            </span>
          </CustomIonButton>
        </template>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
import FooterNavbarMore from "@/components/layout/FooterNavbarMore.vue";
import CustomIonButton from "@/components/primitives/CustomIonButton.vue";
import { MAX_FOOTER_ITEMS } from "@/constants/appSettings";
import { hapticSelection } from "@/methods/haptics";
import { getTablerIconClass, useAppNavigation } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import type { IonModalProps, MenuItem } from "@/types/global";
import { storeToRefs } from "pinia";
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "FooterNavbar",
  components: { CustomIonButton, FooterNavbarMore },
  setup() {
    const currentRoute = useRoute();
    const { navigateTo } = useAppNavigation();
    const appSettingsStore = useAppSettingsStore();
    const { AppMenu, IsKeyboardOpen, IsAppFullscreen } = storeToRefs(appSettingsStore);
    const sessionStore = useSessionStore();
    const { IsMustChangePasswordActive } = storeToRefs(sessionStore);
    const isMoreOpen = ref(false);

    const hideNavigationBar = computed(() => {
      return IsKeyboardOpen.value || IsAppFullscreen.value;
    });
    const footerItems = computed(() => AppMenu.value?.FooterItems ?? []);
    const hasOverflow = computed(() => footerItems.value.length > MAX_FOOTER_ITEMS);
    const primaryItems = computed(() =>
      hasOverflow.value ? footerItems.value.slice(0, MAX_FOOTER_ITEMS - 1) : footerItems.value,
    );
    const overflowItems = computed(() =>
      hasOverflow.value ? footerItems.value.slice(MAX_FOOTER_ITEMS - 1) : [],
    );

    async function onNavClick(item: MenuItem) {
      await hapticSelection();
      isMoreOpen.value = false;
      if (item.ViewRoute) {
        navigateTo({ path: item.ViewRoute });
      }
    }

    async function onMoreClick() {
      isMoreOpen.value = true;
      const componentProps = {
        options: overflowItems.value,
        onSelect: async (option: MenuItem) => await onNavClick(option),
      };
      const modalProps: Partial<IonModalProps> = {
        onModalDismiss: () => {
          isMoreOpen.value = false;
        },
      };
      await useIonModalStore().openSheetModal(FooterNavbarMore, componentProps, modalProps);
    }

    function closeMore() {
      isMoreOpen.value = false;
    }

    return {
      AppMenu,
      IsMustChangePasswordActive,
      currentRoute,
      hideNavigationBar,
      isMoreOpen,
      primaryItems,
      hasOverflow,
      getTablerIconClass,
      onNavClick,
      onMoreClick,
      closeMore,
    };
  },
});
</script>

<style lang="scss" scoped>
.nav-dock {
  background: oklch(from var(--color-light) l c h / 0.5);
  box-shadow:
    0 0.5rem 1rem oklch(from var(--color-primary) l c h / 0.5),
    inset 0 1rem 0 oklch(from var(--color-light) l c h / 0.33);
  & .nav-tab {
    &::part(native) {
      background-color: var(--color-transparent);
      color: var(--color-text-light);
      padding: var(--control-pad-y-large);
      border-radius: var(--rounded-radius);
    }
    &.nav-tab--active::part(native) {
      background-color: var(--color-primary-bg);
      color: var(--color-primary);
    }
  }
  & .nav-divider {
    background-color: var(--color-medium-light);
  }
}
</style>
